import React from 'react';
import { ArrowRight, Lock } from 'lucide-react';
import { A3Seite } from './A3Seite';
import { Abschnitt, AbschnittLeer, AbschnittTabelle, AbschnittZeile, AbschnittZelle } from './Abschnitt';
import { FristZeichen, NachweisStatusZeichen } from './StatusBadge';
import { HANDBUCH_FARBEN } from './stil';
import { formatDatum } from '../../../services/assignmentLogic';
import { baueVerzeichnis, fristLage, offeneNachweise } from '../handbuchLogic';
import { ablaufStatus, kategorieLabel } from '../../dokumente/dokumenteLogic';
import type { ProjektDokument } from '../../dokumente/types';
import type { HandbuchGewerk, HandbuchNachweis } from '../types';

/**
 * Kapitel „Dokumentenverzeichnis" — was zum Handbuch gehört und wo es liegt.
 *
 * NEU geschrieben, nicht portiert. Die Quell-App (`pages/editor/
 * DokumentenverzeichnisPage.tsx`) sammelt ihr Verzeichnis aus den Dokumenten,
 * die an den Gewerken selbst hängen — sie hat keine Ablage. OS-IA hat eine:
 * `project_documents` (Migration Nr. 37) mit Datei, Kategorie, Stand,
 * Gültigkeit und Admin-Schloss. Ein zweites Verzeichnis daneben zu führen
 * hieße, dieselbe Datei an zwei Stellen zu pflegen.
 *
 * ═══ WAS IN DIESES VERZEICHNIS KOMMT ═══════════════════════════════════════
 *   1. Alle Dokumente der Projektablage, die dem Modul „handbuch" zugeordnet
 *      sind (`project_documents.module @> ARRAY['handbuch']`).
 *   2. Zusätzlich jedes Dokument, auf das ein `handbuch_nachweise.dokument_id`
 *      zeigt — auch wenn es dem Modul nicht zugeordnet ist. Sonst führte das
 *      Verzeichnis einen Nachweis als „vorhanden", ohne den Beleg zu nennen.
 *
 * ═══ DAS ADMIN-SCHLOSS ═════════════════════════════════════════════════════
 * `nur_admin` heißt: das Dokument existiert für Nicht-Admins nicht — die Zeile
 * fällt serverseitig aus jedem `select`. Für einen Admin kommt sie hier aber
 * an, und dieses Blatt wird gedruckt und ausgehändigt. Ein gesperrtes Dokument
 * darf deshalb NICHT durchscheinen: es steht mit Titel und Dateinamen nirgends
 * im Verzeichnis, sondern erscheint ausschließlich als Zahl in einer Fußnote.
 * Verschweigen wäre falsch (dann fehlte im Verzeichnis unbemerkt etwas),
 * Auflisten wäre schlimmer (dann stünde der gesperrte Inhalt auf Papier).
 *
 * Die Seite SCHREIBT NICHTS.
 */

// ═══════════════════════════════════════════════════════════════════════════
// REINE FUNKTIONEN
//
// Gehören fachlich nach `handbuchLogic.ts`; dort liegt die geprüfte Stelle (es
// gibt kein jsdom, eine Komponente ist nicht testbar). Sie stehen hier oben und
// sind ausdrücklich NICHT exportiert — `react-refresh/only-export-components`
// lässt in einer Komponentendatei keine Wertexporte zu.
// ═══════════════════════════════════════════════════════════════════════════

// ═══════════════════════════════════════════════════════════════════════════
// DARSTELLUNG
// ═══════════════════════════════════════════════════════════════════════════

/** Gültigkeitsangabe mit Warnfarbe. `null` von `ablaufStatus` heißt „läuft
 *  nicht ab" — der Normalfall und ausdrücklich kein Mangel. */
const Gueltigkeit: React.FC<{ bis?: string | null; heute: string }> = ({ bis, heute }) => {
    const status = ablaufStatus(bis, heute);
    if (!status) return <span style={{ color: HANDBUCH_FARBEN.textLeise }}>—</span>;
    const farbe = status === 'abgelaufen'
        ? HANDBUCH_FARBEN.statusFehlt
        : status === 'laeuft_ab' ? HANDBUCH_FARBEN.statusOffen : HANDBUCH_FARBEN.text;
    return (
        <span style={{ color: farbe, fontWeight: status === 'gueltig' ? 400 : 700 }}>
            {formatDatum(bis)}
            {status === 'abgelaufen' && ' (abgelaufen)'}
        </span>
    );
};

interface Props {
    /** Kopfzeile der Seite, üblicherweise ein `<SeitenKopf …/>`. */
    kopf?: React.ReactNode;
    /** Fußzeile der Seite, üblicherweise ein `<SeitenFuss …/>`. */
    fuss?: React.ReactNode;
    /** Alle Dokumente der Projektablage (`fetchProjektDokumente`). Gefiltert
     *  wird hier — der Dienst liefert das Projekt vollständig. */
    dokumente: ProjektDokument[];
    /** Alle Nachweise des Handbuchs, über alle Gewerkeseiten hinweg. */
    nachweise: HandbuchNachweis[];
    /** Für die Spalte „gefordert von" — welcher Gewerkeseite ein Nachweis
     *  gehört. */
    gewerke: HandbuchGewerk[];
    /** Heutiges ISO-Tagesdatum für Fristen und Gültigkeiten. */
    heuteIso: string;
    /** `true`, wenn Migration Nr. 37 fehlt: dann ist die Ablage leer, weil es
     *  sie nicht gibt — und nicht, weil nichts abgelegt wurde. */
    ablageFehlt?: boolean;
    /** Sprung in die Projektablage. Fehlt der Rückruf, entfällt der Knopf. */
    onAblageOeffnen?: () => void;
}

export const Dokumentenverzeichnis: React.FC<Props> = ({
    kopf, fuss, dokumente, nachweise, gewerke, heuteIso, ablageFehlt = false, onAblageOeffnen,
}) => {
    // `kategorieLabel` wird hereingereicht, damit `handbuchLogic.ts` die
    // Kataloge der Projektablage nicht kennen muss.
    const { eintraege, gesperrt, belegFehlt } = baueVerzeichnis<ProjektDokument>(dokumente, nachweise, kategorieLabel);
    const offen = offeneNachweise(nachweise);
    const gewerkNach = new Map<string, HandbuchGewerk>((gewerke || []).map(g => [g.id, g]));
    const gewerkName = (n: HandbuchNachweis): string =>
        gewerkNach.get(n.handbuch_gewerk_id)?.gewerk_label || '—';

    return (
        <A3Seite kopf={kopf} fuss={fuss}>
            <div style={{ marginBottom: '6px' }} className="flex items-start justify-between gap-3">
                <div>
                    <div style={{ fontSize: '11px', fontWeight: 700, letterSpacing: '0.05em' }}>
                        DOKUMENTENVERZEICHNIS
                    </div>
                    <div style={{ fontSize: '8px', color: HANDBUCH_FARBEN.textLeise, fontStyle: 'italic' }}>
                        Dokumente der Projektablage mit Bezug zum Handbuch sowie alle Belege, auf die
                        ein Nachweis verweist.
                    </div>
                </div>
                {onAblageOeffnen && (
                    <button
                        type="button"
                        onClick={onAblageOeffnen}
                        className="print:hidden inline-flex items-center gap-1 px-2 py-1 rounded border border-slate-300 bg-white text-[10px] font-bold text-slate-700 hover:bg-slate-100"
                    >
                        Zur Projektablage
                        <ArrowRight size={12} />
                    </button>
                )}
            </div>

            <div className="flex-1 min-h-0 flex flex-col gap-[6px]">
                <Abschnitt
                    titel="Abgelegte Dokumente"
                    zusatz={`${eintraege.length}`}
                    className="flex-1"
                >
                    {eintraege.length === 0 ? (
                        <AbschnittLeer
                            text={ablageFehlt
                                ? 'Die Projektablage steht in dieser Umgebung noch nicht zur Verfügung (Migration Nr. 37).'
                                : 'Der Projektablage ist noch kein Dokument mit Bezug zum Handbuch zugeordnet. Beim Hochladen das Modul „SiGe-Handbuch" ankreuzen.'}
                        />
                    ) : (
                        <AbschnittTabelle
                            kopf={['Titel', 'Kategorie', 'Stand', 'gültig bis', 'Dateiname', 'Nachweis-Status']}
                            breiten={['26%', '13%', '9%', '11%', '22%', '19%']}
                        >
                            {eintraege.map((e, i) => (
                                <AbschnittZeile key={e.dokument.id} erste={i === 0}>
                                    <AbschnittZelle>
                                        {e.dokument.titel}
                                        {!e.ueberModul && (
                                            /* Kam nur über einen Nachweis herein. Das gehört
                                               dazugesagt: wer die Ablage nach dem Modul
                                               „Handbuch" filtert, findet dieses Dokument nicht. */
                                            <div style={{ fontSize: '7px', color: HANDBUCH_FARBEN.textLeise }}>
                                                nur über einen Nachweis verknüpft
                                            </div>
                                        )}
                                    </AbschnittZelle>
                                    <AbschnittZelle>{kategorieLabel(e.dokument.kategorie)}</AbschnittZelle>
                                    <AbschnittZelle>{e.dokument.stand || '—'}</AbschnittZelle>
                                    <AbschnittZelle>
                                        <Gueltigkeit bis={e.dokument.gueltig_bis} heute={heuteIso} />
                                    </AbschnittZelle>
                                    <AbschnittZelle>{e.dokument.dateiname}</AbschnittZelle>
                                    <AbschnittZelle>
                                        {e.nachweise.length === 0 ? (
                                            <span style={{ color: HANDBUCH_FARBEN.textLeise }}>
                                                keinem Nachweis zugeordnet
                                            </span>
                                        ) : e.nachweise.map(n => (
                                            <div key={n.id}>
                                                <NachweisStatusZeichen status={n.status} mitText />
                                                {' '}
                                                <FristZeichen lage={fristLage(n, heuteIso)} />
                                                <div style={{ fontSize: '7px', color: HANDBUCH_FARBEN.textLeise }}>
                                                    {gewerkName(n)}: {n.bezeichnung}
                                                </div>
                                            </div>
                                        ))}
                                    </AbschnittZelle>
                                </AbschnittZeile>
                            ))}
                        </AbschnittTabelle>
                    )}
                </Abschnitt>

                <Abschnitt
                    titel="Geforderte Nachweise ohne hinterlegtes Dokument"
                    zusatz={`${offen.length}`}
                    className="flex-1"
                >
                    {offen.length === 0 ? (
                        <AbschnittLeer text={
                            (nachweise || []).length === 0
                                ? 'Es ist noch kein Nachweis gefordert — die Gewerkeseiten sind hierzu leer.'
                                : 'Zu jedem geforderten Nachweis liegt ein Dokument in der Ablage.'
                        } />
                    ) : (
                        <AbschnittTabelle
                            kopf={['Gewerk', 'Nr', 'Nachweis', 'Status', 'Frist', 'Bemerkung']}
                            breiten={['20%', '6%', '30%', '13%', '14%', '17%']}
                        >
                            {offen.map((n, i) => (
                                <AbschnittZeile key={n.id} erste={i === 0}>
                                    <AbschnittZelle>{gewerkName(n)}</AbschnittZelle>
                                    <AbschnittZelle>{n.nr}</AbschnittZelle>
                                    <AbschnittZelle>{n.bezeichnung}</AbschnittZelle>
                                    <AbschnittZelle>
                                        <NachweisStatusZeichen status={n.status} mitText />
                                    </AbschnittZelle>
                                    <AbschnittZelle>
                                        {formatDatum(n.frist) || '—'}
                                        <div><FristZeichen lage={fristLage(n, heuteIso)} /></div>
                                    </AbschnittZelle>
                                    <AbschnittZelle>
                                        {n.status === 'vorhanden' ? (
                                            /* Der unangenehmste Fall: es sieht erledigt aus und
                                               ist nicht belegbar. */
                                            <span style={{ color: HANDBUCH_FARBEN.risikoMittel }}>
                                                als vorhanden geführt, ohne Beleg in der Ablage
                                            </span>
                                        ) : n.bemerkung || ''}
                                    </AbschnittZelle>
                                </AbschnittZeile>
                            ))}
                        </AbschnittTabelle>
                    )}
                </Abschnitt>
            </div>

            {/* Fußnoten: alles, was das Verzeichnis unvollständig macht, wird
                BENANNT — mit Zahl, ohne Inhalt. */}
            <div
                style={{
                    borderTop: `1px solid ${HANDBUCH_FARBEN.linie}`,
                    marginTop: '5px',
                    paddingTop: '3px',
                    fontSize: '7.5px',
                    color: HANDBUCH_FARBEN.textLeise,
                }}
            >
                {gesperrt > 0 && (
                    <div className="flex items-center gap-1" style={{ color: HANDBUCH_FARBEN.risikoMittel }}>
                        <Lock size={9} />
                        {gesperrt} zugehörige(s) Dokument(e) trägt/tragen ein Admin-Schloss und ist/sind
                        deshalb hier weder mit Titel noch mit Dateinamen aufgeführt. Einsicht nur über
                        die Projektablage.
                    </div>
                )}
                {belegFehlt.length > 0 && (
                    <div style={{ color: HANDBUCH_FARBEN.risikoHoch }}>
                        {belegFehlt.length} Nachweis(e) verweisen auf ein Dokument, das in der Ablage
                        nicht (mehr) erreichbar ist: {belegFehlt.map(n => n.bezeichnung).join(', ')}.
                    </div>
                )}
                <div>
                    Aufgenommen werden Dokumente mit dem Modulschlüssel „SiGe-Handbuch" und alle, auf
                    die ein Nachweis verweist. Ein Dokument, das nur in einem anderen Modul liegt,
                    steht hier nicht — es fehlt deshalb nicht, es gehört nur woanders hin.
                </div>
            </div>
        </A3Seite>
    );
};
